import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Save, RefreshCw, Upload, ExternalLink } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

// Helper function to get full image URL - fixes URLs from old domains
const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.includes('/api/uploads/')) {
    const match = url.match(/\/api\/uploads\/[^\s"']+/);
    if (match) return `${window.location.origin}${match[0]}`;
  }
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const CMSPartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try { const res = await api.get('/cms/partners'); setPartners(res.data); }
    catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    const { data } = editModal;
    try {
      if (data.id) { await api.put(`/cms/partners/${data.id}`, data); }
      else { await api.post('/cms/partners', data); }
      toast.success('Enregistré'); loadData(); setEditModal({ open: false, data: null });
    } catch (error) { toast.error('Erreur'); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/cms/partners/${deleteModal.id}`); toast.success('Supprimé'); loadData(); setDeleteModal({ open: false, id: null }); }
    catch (error) { toast.error('Erreur'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; 
    if (!file) return;
    
    const formData = new FormData(); 
    formData.append('file', file); 
    formData.append('folder', 'partners');
    
    try { 
      const res = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }); 
      setEditModal({...editModal, data: {...editModal.data, logo_url: res.data.url}}); 
      toast.success('Logo uploadé'); 
    }
    catch (error) { 
      console.error('Upload error:', error);
      toast.error('Erreur upload'); 
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">Partenaires</h1><p className="text-slate-500">Logos des partenaires qui défilent sur le site</p></div>
        <Button onClick={() => setEditModal({ open: true, data: { is_active: true, order: partners.length + 1 } })} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Plus className="w-4 h-4 mr-2" />Nouveau partenaire</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-6">
        {partners.map(partner => (
          <Card key={partner.id}>
            <CardContent className="pt-6 text-center">
              <div className="h-16 flex items-center justify-center mb-2">
                {partner.logo_url && <img src={getFullImageUrl(partner.logo_url)} alt={partner.name} className="max-h-full max-w-full object-contain" />}
              </div>
              <p className="text-sm font-medium truncate">{partner.name}</p>
              {partner.website_url && partner.website_url !== '#' && (
                <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 hover:underline flex items-center justify-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" />Site web
                </a>
              )}
              <div className="mt-3 flex justify-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: true, data: partner })}><Edit className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, id: partner.id })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, data: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editModal.data?.id ? 'Modifier' : 'Nouveau'} partenaire</DialogTitle></DialogHeader>
          {editModal.data && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Nom</Label><Input value={editModal.data.name || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, name: e.target.value}})} /></div>
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex gap-2 items-center">
                  <Input value={editModal.data.logo_url || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, logo_url: e.target.value}})} placeholder="URL ou upload" className="flex-1" />
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                    <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-slate-50 transition-colors">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">Upload</span>
                    </div>
                  </label>
                </div>
                {editModal.data.logo_url && (
                  <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                    <img src={getFullImageUrl(editModal.data.logo_url)} alt="Preview" className="h-16 object-contain mx-auto" />
                  </div>
                )}
              </div>
              <div className="space-y-2"><Label>Site web (optionnel)</Label><Input value={editModal.data.website_url || ''} onChange={(e) => setEditModal({...editModal, data: {...editModal.data, website_url: e.target.value}})} placeholder="https://..." /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditModal({ open: false, data: null })}>Annuler</Button><Button onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-blue-600"><Save className="w-4 h-4 mr-2" />Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, id: null })}>
        <DialogContent><DialogHeader><DialogTitle>Supprimer ce partenaire ?</DialogTitle></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, id: null })}>Annuler</Button><Button variant="destructive" onClick={handleDelete}>Supprimer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPartnersPage;
